import React, { useState } from 'react';
import ListItem from '@material-ui/core/ListItem';
import IconButton from '@material-ui/core/IconButton';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Card from '@material-ui/core/Card';
import CardActionArea from '@material-ui/core/CardActionArea';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import LowPriorityIcon from '@material-ui/icons/LowPriority';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import ImageIcon from '@material-ui/icons/Image';
import DescriptionIcon from '@material-ui/icons/Description';
import GridOnIcon from '@material-ui/icons/GridOn';
import SlideshowIcon from '@material-ui/icons/Slideshow';
import MovieIcon from '@material-ui/icons/Movie';
import MusicNoteIcon from '@material-ui/icons/MusicNote';
import ArchiveIcon from '@material-ui/icons/Archive';
import CodeIcon from '@material-ui/icons/Code';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ScheduleIcon from '@material-ui/icons/Schedule';
import moment from 'moment';
import JWTDecoder from 'jwt-decode';
import { useSnackbar } from 'notistack';

import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog';
import Api from '../../../Api';

// Mapa extensión -> icono + color. Extensión desconocida => icono genérico.
const EXT_ICONS = {
  pdf: { Icon: PictureAsPdfIcon, color: '#e53935' },
  jpg: { Icon: ImageIcon, color: '#43a047' },
  jpeg: { Icon: ImageIcon, color: '#43a047' },
  png: { Icon: ImageIcon, color: '#43a047' },
  gif: { Icon: ImageIcon, color: '#43a047' },
  bmp: { Icon: ImageIcon, color: '#43a047' },
  svg: { Icon: ImageIcon, color: '#43a047' },
  webp: { Icon: ImageIcon, color: '#43a047' },
  doc: { Icon: DescriptionIcon, color: '#1e88e5' },
  docx: { Icon: DescriptionIcon, color: '#1e88e5' },
  txt: { Icon: DescriptionIcon, color: '#607d8b' },
  rtf: { Icon: DescriptionIcon, color: '#1e88e5' },
  odt: { Icon: DescriptionIcon, color: '#1e88e5' },
  xls: { Icon: GridOnIcon, color: '#2e7d32' },
  xlsx: { Icon: GridOnIcon, color: '#2e7d32' },
  csv: { Icon: GridOnIcon, color: '#2e7d32' },
  ods: { Icon: GridOnIcon, color: '#2e7d32' },
  ppt: { Icon: SlideshowIcon, color: '#fb8c00' },
  pptx: { Icon: SlideshowIcon, color: '#fb8c00' },
  mp4: { Icon: MovieIcon, color: '#8e24aa' },
  avi: { Icon: MovieIcon, color: '#8e24aa' },
  mov: { Icon: MovieIcon, color: '#8e24aa' },
  mkv: { Icon: MovieIcon, color: '#8e24aa' },
  wmv: { Icon: MovieIcon, color: '#8e24aa' },
  mp3: { Icon: MusicNoteIcon, color: '#d81b60' },
  wav: { Icon: MusicNoteIcon, color: '#d81b60' },
  ogg: { Icon: MusicNoteIcon, color: '#d81b60' },
  flac: { Icon: MusicNoteIcon, color: '#d81b60' },
  zip: { Icon: ArchiveIcon, color: '#6d4c41' },
  rar: { Icon: ArchiveIcon, color: '#6d4c41' },
  '7z': { Icon: ArchiveIcon, color: '#6d4c41' },
  tar: { Icon: ArchiveIcon, color: '#6d4c41' },
  gz: { Icon: ArchiveIcon, color: '#6d4c41' },
  js: { Icon: CodeIcon, color: '#f9a825' },
  ts: { Icon: CodeIcon, color: '#f9a825' },
  html: { Icon: CodeIcon, color: '#f9a825' },
  css: { Icon: CodeIcon, color: '#f9a825' },
  json: { Icon: CodeIcon, color: '#f9a825' },
  xml: { Icon: CodeIcon, color: '#f9a825' }
};

const getFileIcon = (name, big) => {
  const parts = (name || '').split('.');
  const ext = parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
  const match = EXT_ICONS[ext];
  const sizeStyle = big ? { fontSize: 40 } : {};
  if (match) {
    const Icon = match.Icon;
    return <Icon style={{ color: match.color, ...sizeStyle }} />;
  }
  return <InsertDriveFileIcon style={{ color: '#90a4ae', ...sizeStyle }} />;
};

// isManager: roles 1,2 see the management controls. Clients (role 3) download only.
// isAdmin: only role 1 sees the delete action (drive deletes are admin-only).
// viewMode: 'list' (default) | 'grid'. onRename/onMove: manager callbacks (file).
const FileItem = ({ file, fileDeleted, isManager, isAdmin = false, viewMode = 'list', onRename, onMove }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [confirmDeleteDialogVisibility, setConfirmDeleteDialogVisibility] = useState(false);
  // Descarga propia registrada en esta sesión (role 3), hasta que se recargue el contenido.
  const [localDownload, setLocalDownload] = useState(null);

  const jwt = localStorage.getItem('access-jwt');
  var jwtUser = [];
  if (jwt !== null) {
    jwtUser = JWTDecoder(jwt);
  }

  // El driver mysql2 (DB_TIMEZONE=-03:00) le aplica un offset extra a los
  // CURRENT_TIMESTAMP que ya vienen en UTC, dando +3h. Compensamos -3h para
  // mostrar la hora real de Argentina (workaround acotado a esta vista).
  const uploadedAt = file.created_on ? moment.utc(file.created_on).subtract(3, 'hours').local().format('DD/MM/YYYY HH:mm') : '';

  // Mismo workaround -3h que uploadedAt / ReportFileItem.
  const formatDownloadedOn = downloadedOn => {
    if (!downloadedOn) {
      return '';
    }
    return moment.utc(downloadedOn).subtract(3, 'hours').local().format('DD/MM/YYYY HH:mm');
  };

  const downloaderName = download => {
    if (download.user) {
      var fullName = [download.user.firstname, download.user.lastname].filter(Boolean).join(' ');
      if (fullName) {
        return fullName;
      }
      return download.user.email || 'Usuario';
    }
    return download.user_name || 'Usuario';
  };

  // Role 3 only receives its own download from the API; admin/operador see all of them.
  const serverDownloads = file.downloads || [];
  const downloadedByMe = serverDownloads.some(d => Number(d.user_id) === Number(jwtUser.u_id));
  const downloads = localDownload && !downloadedByMe ? serverDownloads.concat([localDownload]) : serverDownloads;

  // Best-effort: fire-and-forget, never blocks nor conditions opening the file.
  // Managers get a 200 no-op from the API, so the call is skipped for them.
  const recordDownload = () => {
    if (isManager) {
      return;
    }
    Api.post('/drive_files/' + file.id + '/download')
      .then(function() {
        if (!downloadedByMe) {
          setLocalDownload({
            user_id: jwtUser.u_id,
            user_name: jwtUser.u_complete_name || jwtUser.u_email,
            downloaded_on_label: moment().format('DD/MM/YYYY HH:mm')
          });
        }
      })
      .catch(function() {});
  };

  const handleOpenFile = () => {
    // Files are public-read; the contents endpoint already returns the url.
    // window.open MUST run synchronously inside the click handler (popup blockers),
    // the download record is fired afterwards.
    if (file.url) {
      window.open(file.url, '_blank');
      recordDownload();
      return;
    }
    // Fall back to GET /drive_files/:id when the url is not present.
    Api.get('/drive_files/' + file.id)
      .then(function(response) {
        const url = response.data && response.data.data ? response.data.data.url : null;
        if (url) {
          window.open(url, '_blank');
          recordDownload();
        }
      })
      .catch(function() {
        enqueueSnackbar('No se pudo abrir el archivo, intenta nuevamente.', {
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
          variant: 'error'
        });
      });
  };

  const handleRename = event => {
    event.stopPropagation();
    if (onRename) onRename(file);
  };

  const handleMove = event => {
    event.stopPropagation();
    if (onMove) onMove(file);
  };

  const handleDeleteFile = event => {
    event.preventDefault();
    event.stopPropagation();
    setConfirmDeleteDialogVisibility(true);
  };

  const handleCloseConfirmDeleteDialog = () => {
    setConfirmDeleteDialogVisibility(false);
  };

  const handleOnConfirmDelete = () => {
    setConfirmDeleteDialogVisibility(false);
    Api.delete('/drive_files/' + file.id)
      .then(function() {
        fileDeleted();
        enqueueSnackbar('Archivo "' + file.name + '" removido exitosamente!', {
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
          variant: 'success'
        });
      })
      .catch(function(error) {
        var message = 'Error de conexion, contacta a soporte';
        if (error.response) {
          message = 'Ocurrio un error, intenta nuevamente.';
        }
        enqueueSnackbar(message, {
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
          variant: 'error'
        });
      });
  };

  const managerActions = isManager ? (
    <React.Fragment>
      <Tooltip title="Renombrar">
        <IconButton size="small" onClick={handleRename}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Mover">
        <IconButton size="small" onClick={handleMove}>
          <LowPriorityIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {isAdmin ? (
        <Tooltip title="Eliminar">
          <IconButton size="small" onClick={handleDeleteFile} style={{ color: '#e91e63' }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
    </React.Fragment>
  ) : null;

  const downloadIndicator =
    downloads.length > 0 ? (
      <Tooltip
        title={
          <div>
            {downloads.map((download, i) => (
              <div key={i}>
                Descargado por {downloaderName(download)} -{' '}
                {download.downloaded_on_label || formatDownloadedOn(download.downloaded_on)}
              </div>
            ))}
          </div>
        }>
        <CheckCircleIcon style={{ color: '#29b6f6', marginRight: 8 }} />
      </Tooltip>
    ) : (
      <Tooltip title="No descargado">
        <ScheduleIcon style={{ color: '#9e9e9e', marginRight: 8 }} />
      </Tooltip>
    );

  const confirmDialog = (
    <ConfirmDialog
      handleClose={handleCloseConfirmDeleteDialog}
      handleOnConfirm={handleOnConfirmDelete}
      visibility={confirmDeleteDialogVisibility}
      title="¿Está seguro que desea eliminar este archivo?"
      message="La eliminación de este archivo es permanente"
    />
  );

  if (viewMode === 'grid') {
    return (
      <Card variant="outlined" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Tooltip title={uploadedAt ? file.name + ' — ' + uploadedAt : file.name}>
          <CardActionArea onClick={handleOpenFile} style={{ flexGrow: 1, padding: 12, textAlign: 'center' }}>
            {getFileIcon(file.name, true)}
            <Typography variant="caption" component="div" noWrap style={{ marginTop: 4 }}>
              {file.name}
            </Typography>
          </CardActionArea>
        </Tooltip>
        <div style={{ textAlign: 'center', paddingTop: 4 }}>{downloadIndicator}</div>
        {isManager ? <div style={{ textAlign: 'center', paddingBottom: 4 }}>{managerActions}</div> : null}
        {confirmDialog}
      </Card>
    );
  }

  return (
    <div>
      <ListItem key={file.id} button onClick={handleOpenFile}>
        <ListItemIcon>{getFileIcon(file.name)}</ListItemIcon>
        <ListItemText primary={file.name} />
        {uploadedAt ? (
          <Typography variant="body2" color="textSecondary" style={{ whiteSpace: 'nowrap', marginLeft: 16, marginRight: 8 }}>
            {uploadedAt}
          </Typography>
        ) : null}
        {downloadIndicator}
        {managerActions}
      </ListItem>
      {confirmDialog}
    </div>
  );
};

export default FileItem;
